import React from "react";
import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, LayoutDashboard, Smartphone, LineChart, Bot,
  Route, CalendarClock, MessageSquareText, PenLine, Boxes, ClipboardCheck, ShieldCheck } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { useLang } from "../i18n/LanguageContext";

import { IMAGES, PORTAL_FEATURES } from "../data";

const PLATFORM = [
  { icon: Sparkles, title: "AI Quote Generator", desc: "Instant, data-driven estimates — labour hours, staffing, and pricing generated in seconds.", live: true, to: "/ai-estimate" },
  { icon: LayoutDashboard, title: "Client Portal", desc: "Real-time visibility into services, invoices, contracts, and before/after photos.", live: false },
  { icon: LineChart, title: "Quality Dashboard", desc: "Digital inspections and live quality scores keep every site accountable.", live: false },
  { icon: Smartphone, title: "Employee Mobile App", desc: "GPS clock-in, checklists, photo capture and signatures — all in the field.", live: false },
];

const AI_MODULES = [
  { icon: MessageSquareText, title: "AI Customer Support" },
  { icon: CalendarClock, title: "AI Scheduling" },
  { icon: Route, title: "AI Route Optimization" },
  { icon: Bot, title: "AI Quote Assistant" },
  { icon: ClipboardCheck, title: "AI Cleaning Checklist" },
  { icon: ShieldCheck, title: "AI Staff Assignment" },
  { icon: Boxes, title: "AI Inventory Forecast" },
  { icon: PenLine, title: "AI Email & Proposal Writer" },
];

export default function Technology() {
  const { t } = useLang();
  return (
    <div data-testid="technology-page" className="bg-ink-950">
      <Seo title="Technology" path="/technology" description="North Peak's technology — AI estimating, client portal, and facility management tools serving Montreal and the Greater Montreal Area." keywords="Facility Services Montreal, Cleaning Company Montreal" />
      <PageHero
        overline={t("Technology")}
        title={t("Facility management, engineered like software.")}
        subtitle={t("North Peak pairs elite crews with a technology platform that makes quality measurable, scheduling effortless, and communication instant.")}
        image={IMAGES.glassCurve}
      />

      <section className="py-24 md:py-32 relative overflow-hidden glow">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Reveal><p className="overline text-steel-light mb-5">{t("The Platform")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-5xl leading-tight mb-14 max-w-2xl" style={{ fontWeight: 500 }}>{t("One connected system, end to end.")}</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {PLATFORM.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 0.1}>
                <div data-testid={`platform-${i}`} className="glass card-hover rounded-3xl p-9 h-full group">
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-steel/20 transition-colors">
                      <p.icon className="h-6 w-6 text-steel-light" strokeWidth={1.5} />
                    </div>
                    <span className={`text-xs rounded-full px-3 py-1 font-600 ${p.live ? "bg-green-400/15 text-green-300" : "bg-white/8 text-white/50"}`}>
                      {p.live ? t("Live") : t("Coming Soon")}
                    </span>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-white" style={{ fontWeight: 500 }}>{t(p.title)}</h3>
                  <p className="mt-3 text-white/55 leading-relaxed">{t(p.desc)}</p>
                  {p.live && (
                    <Link to={p.to} className="group/btn mt-6 inline-flex items-center gap-2 text-white font-600 text-sm">
                      {t("Try it now")} <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="portal-section" className="bg-ink-950 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><p className="overline text-steel-light mb-5">{t("North Peak Portal")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-5xl leading-tight mb-4 max-w-2xl" style={{ fontWeight: 500 }}>{t("Everything your team needs, in one login.")}</h2></Reveal>
          <Reveal delay={0.15}><p className="text-white/55 leading-relaxed text-lg mb-14 max-w-2xl">{t("A single portal that turns facility management into a transparent, on-demand experience for every client.")}</p></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PORTAL_FEATURES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="glass card-hover rounded-3xl p-8 h-full group">
                  <div className="h-12 w-12 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-steel/20 transition-colors">
                    <p.icon className="h-6 w-6 text-steel-light" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-serif text-xl text-white" style={{ fontWeight: 500 }}>{t(p.title)}</h3>
                  <p className="mt-3 text-white/50 text-sm leading-relaxed">{t(p.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink-900 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <Reveal><p className="overline text-steel-light mb-5">{t("Powered by AI")}</p></Reveal>
              <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-5xl leading-tight" style={{ fontWeight: 500 }}>{t("An AI layer across the entire operation.")}</h2></Reveal>
              <Reveal delay={0.2}><p className="mt-6 text-white/55 leading-relaxed text-lg">{t("From instant quotes to route optimization and predictive inventory, our roadmap brings intelligence to every corner of facility management.")}</p></Reveal>
              <Reveal delay={0.3}>
                <Link to="/ai-estimate" className="btn-pill group inline-flex items-center gap-2 mt-8 bg-white text-ink-950 pl-7 pr-6 py-4 text-sm font-600 hover:shadow-xl hover:shadow-white/20">
                  {t("Experience the AI Quote Generator")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Reveal>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 gap-4">
              {AI_MODULES.map((m, i) => (
                <Reveal key={m.title} delay={(i % 4) * 0.06}>
                  <div className="glass card-hover rounded-2xl p-5 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                      <m.icon className="h-5 w-5 text-steel-light" strokeWidth={1.5} />
                    </div>
                    <span className="text-white/80 text-sm font-500">{t(m.title)}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
